import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BellRing,
  Mail,
  Package2,
  Send,
  ShieldCheck,
  Smartphone,
  Truck,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY_CAMPAIGN = {
  type: "general",
  audience: "all",
  title: "",
  body: "",
  cta_label: "",
  cta_url: "/app",
  category_slug: "",
  tracking_id: "",
  order_number: "",
  target_email: "",
  target_device_id: "",
  send_email: false,
};

const TYPE_LABELS = {
  general: "General",
  product: "Product",
  shipment: "Shipment",
  delivery: "Delivery",
  order: "Order",
};

const AUDIENCE_HELP = {
  all: "Visible to all app users.",
  subscribers: "Targets saved customer subscribers.",
  email: "Targets one customer email.",
  device: "Targets one device ID.",
  tracking: "Targets customers watching a tracking ID.",
  order: "Targets customers watching an order number.",
};

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function AdminAppNotifications() {
  const qc = useQueryClient();
  const [campaign, setCampaign] = useState(EMPTY_CAMPAIGN);
  const [search, setSearch] = useState("");

  const { data: subscriptions = [], isLoading: subscriptionsLoading } = useQuery({
    queryKey: ["admin-app-subscriptions"],
    queryFn: async () => (await api.get("/admin/app/subscriptions")).data,
  });

  const { data: notifications = [], isLoading: notificationsLoading } = useQuery({
    queryKey: ["admin-app-notifications"],
    queryFn: async () => (await api.get("/admin/app/notifications")).data,
  });

  const createNotificationMut = useMutation({
    mutationFn: async (payload) => (await api.post("/admin/app/notifications", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-app-notifications"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setCampaign(EMPTY_CAMPAIGN);
      toast.success("App notification campaign created");
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const stats = useMemo(() => {
    const platformCounts = subscriptions.reduce(
      (acc, item) => {
        const key = item.platform || "web";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      { android: 0, ios: 0, web: 0 }
    );
    return {
      total: subscriptions.length,
      pushReady: subscriptions.filter((item) => item.push_enabled).length,
      emailReady: subscriptions.filter((item) => item.allow_email && item.email).length,
      shipmentWatchers: subscriptions.filter((item) => (item.tracking_ids || []).length).length,
      platformCounts,
    };
  }, [subscriptions]);

  const filteredSubscriptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return subscriptions;
    return subscriptions.filter((item) =>
      [
        item.customer_name,
        item.email,
        item.phone,
        item.platform,
        ...(item.tracking_ids || []),
        ...(item.order_numbers || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [search, subscriptions]);

  const submitCampaign = (event) => {
    event.preventDefault();
    createNotificationMut.mutate({
      ...campaign,
      tracking_id: String(campaign.tracking_id || "").trim().toUpperCase(),
      order_number: String(campaign.order_number || "").trim().toUpperCase(),
      target_email: String(campaign.target_email || "").trim().toLowerCase(),
      target_device_id: String(campaign.target_device_id || "").trim(),
    });
  };

  return (
    <div data-testid="admin-app-notifications">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="badge-purple">
            <BellRing className="h-3.5 w-3.5" /> Mobile Messaging
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            App Notifications
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage customer alert campaigns, monitor subscriber readiness, and support Android
            and iPhone messaging from one place.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Subscribers</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
            {stats.total}
          </div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Push Ready</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
            {stats.pushReady}
          </div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Email Ready</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
            {stats.emailReady}
          </div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Shipment Watchers</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
            {stats.shipmentWatchers}
          </div>
        </div>
      </div>

      <div className="grid xl:grid-cols-[1.05fr_0.95fr] gap-6">
        <div className="rsi-card p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                Campaign composer
              </div>
              <h2 className="font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)] mt-2">
                Send customer app updates
              </h2>
            </div>
            <span className="badge-purple">{AUDIENCE_HELP[campaign.audience]}</span>
          </div>

          <form className="mt-6 grid gap-4" onSubmit={submitCampaign}>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Notification Type</Label>
                <Select
                  value={campaign.type}
                  onValueChange={(value) => setCampaign((current) => ({ ...current, type: value }))}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Audience</Label>
                <Select
                  value={campaign.audience}
                  onValueChange={(value) =>
                    setCampaign((current) => ({ ...current, audience: value }))
                  }
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All app users</SelectItem>
                    <SelectItem value="subscribers">Subscribers</SelectItem>
                    <SelectItem value="email">Specific email</SelectItem>
                    <SelectItem value="device">Specific device</SelectItem>
                    <SelectItem value="tracking">Tracking watchers</SelectItem>
                    <SelectItem value="order">Order watchers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="campaign-title">Title</Label>
              <Input
                id="campaign-title"
                value={campaign.title}
                onChange={(event) =>
                  setCampaign((current) => ({ ...current, title: event.target.value }))
                }
                className="mt-1.5"
                placeholder="Shipment reached customs clearance"
                required
              />
            </div>

            <div>
              <Label htmlFor="campaign-body">Message</Label>
              <Textarea
                id="campaign-body"
                rows={5}
                value={campaign.body}
                onChange={(event) =>
                  setCampaign((current) => ({ ...current, body: event.target.value }))
                }
                className="mt-1.5"
                placeholder="Write a clear customer update for product arrivals, shipment movement, or delivery status."
                required
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="campaign-cta-label">CTA Label</Label>
                <Input
                  id="campaign-cta-label"
                  value={campaign.cta_label}
                  onChange={(event) =>
                    setCampaign((current) => ({ ...current, cta_label: event.target.value }))
                  }
                  className="mt-1.5"
                  placeholder="Track shipment"
                />
              </div>
              <div>
                <Label htmlFor="campaign-cta-url">CTA URL</Label>
                <Input
                  id="campaign-cta-url"
                  value={campaign.cta_url}
                  onChange={(event) =>
                    setCampaign((current) => ({ ...current, cta_url: event.target.value }))
                  }
                  className="mt-1.5"
                  placeholder="/app or /track?id=RSI-2026-0001"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="campaign-category">Category Slug</Label>
                <Input
                  id="campaign-category"
                  value={campaign.category_slug}
                  onChange={(event) =>
                    setCampaign((current) => ({
                      ...current,
                      category_slug: event.target.value.toLowerCase(),
                    }))
                  }
                  className="mt-1.5"
                  placeholder="textiles"
                />
              </div>
              <div>
                <Label htmlFor="campaign-tracking">Tracking ID</Label>
                <Input
                  id="campaign-tracking"
                  value={campaign.tracking_id}
                  onChange={(event) =>
                    setCampaign((current) => ({
                      ...current,
                      tracking_id: event.target.value.toUpperCase(),
                    }))
                  }
                  className="mt-1.5"
                  placeholder="RSI-2026-0001"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="campaign-order">Order Number</Label>
                <Input
                  id="campaign-order"
                  value={campaign.order_number}
                  onChange={(event) =>
                    setCampaign((current) => ({
                      ...current,
                      order_number: event.target.value.toUpperCase(),
                    }))
                  }
                  className="mt-1.5"
                  placeholder="RSI-ORD-202606..."
                />
              </div>
              <div>
                <Label htmlFor="campaign-email">Target Email</Label>
                <Input
                  id="campaign-email"
                  type="email"
                  value={campaign.target_email}
                  onChange={(event) =>
                    setCampaign((current) => ({
                      ...current,
                      target_email: event.target.value.toLowerCase(),
                    }))
                  }
                  className="mt-1.5"
                  placeholder="customer@example.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="campaign-device">Target Device ID</Label>
              <Input
                id="campaign-device"
                value={campaign.target_device_id}
                onChange={(event) =>
                  setCampaign((current) => ({ ...current, target_device_id: event.target.value }))
                }
                className="mt-1.5"
                placeholder="device-..."
              />
            </div>

            <label className="inline-flex items-center gap-3 rounded-2xl border border-gray-200 bg-[var(--rsi-bg-subtle)] px-4 py-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={campaign.send_email}
                onChange={(event) =>
                  setCampaign((current) => ({ ...current, send_email: event.target.checked }))
                }
              />
              Also send email where allowed
            </label>

            <button
              type="submit"
              className="btn-primary"
              disabled={createNotificationMut.isPending}
            >
              <Send className="h-4 w-4" />
              {createNotificationMut.isPending ? "Sending..." : "Create campaign"}
            </button>
          </form>
        </div>

        <div className="space-y-6">
          <div className="rsi-card p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                  Platform split
                </div>
                <h2 className="font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)] mt-2">
                  Android and iPhone readiness
                </h2>
              </div>
              <Smartphone className="h-5 w-5 text-[var(--rsi-purple)]" />
            </div>

            <div className="mt-5 grid sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-3xl bg-[var(--rsi-bg-subtle)] p-4">
                <div className="text-gray-400 uppercase tracking-wider text-xs">Android</div>
                <div className="mt-2 font-display text-2xl font-black text-[var(--rsi-navy)]">
                  {stats.platformCounts.android}
                </div>
              </div>
              <div className="rounded-3xl bg-[var(--rsi-bg-subtle)] p-4">
                <div className="text-gray-400 uppercase tracking-wider text-xs">iPhone</div>
                <div className="mt-2 font-display text-2xl font-black text-[var(--rsi-navy)]">
                  {stats.platformCounts.ios}
                </div>
              </div>
              <div className="rounded-3xl bg-[var(--rsi-bg-subtle)] p-4">
                <div className="text-gray-400 uppercase tracking-wider text-xs">Web</div>
                <div className="mt-2 font-display text-2xl font-black text-[var(--rsi-navy)]">
                  {stats.platformCounts.web}
                </div>
              </div>
            </div>
          </div>

          <div className="rsi-card p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                  Recent campaigns
                </div>
                <h2 className="font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)] mt-2">
                  Notification history
                </h2>
              </div>
              <BellRing className="h-5 w-5 text-[var(--rsi-purple)]" />
            </div>

            <div className="mt-5 space-y-3">
              {notificationsLoading ? (
                <div className="text-sm text-gray-500">Loading campaigns...</div>
              ) : notifications.length === 0 ? (
                <div className="text-sm text-gray-500">No app notifications created yet.</div>
              ) : (
                notifications.slice(0, 8).map((item) => (
                  <div key={item.id} className="rounded-3xl border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          {TYPE_LABELS[item.type] || item.type} · {item.audience}
                        </div>
                        <div className="font-semibold text-[var(--rsi-navy)] mt-1">
                          {item.title}
                        </div>
                      </div>
                      <div className="text-[11px] text-gray-400">{formatDate(item.created_at)}</div>
                    </div>
                    <div className="mt-2 text-sm text-gray-600 whitespace-pre-line">
                      {item.body}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {item.send_email ? (
                        <span className="badge-purple">
                          <Mail className="h-3.5 w-3.5" /> Email enabled
                        </span>
                      ) : null}
                      {item.tracking_id ? (
                        <span className="badge-purple">
                          <Truck className="h-3.5 w-3.5" /> {item.tracking_id}
                        </span>
                      ) : null}
                      {item.order_number ? (
                        <span className="badge-purple">
                          <ShieldCheck className="h-3.5 w-3.5" /> {item.order_number}
                        </span>
                      ) : null}
                      {item.category_slug ? (
                        <span className="badge-purple">
                          <Package2 className="h-3.5 w-3.5" /> {item.category_slug}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rsi-card p-6 mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
              Subscriber directory
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)] mt-2">
              Customer notification readiness
            </h2>
          </div>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, email, phone, tracking, order..."
            className="w-full max-w-sm"
          />
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--rsi-bg-subtle)] text-left">
              <tr>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Customer</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Platform</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Permissions</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Watch Lists</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {subscriptionsLoading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-500">
                    Loading subscribers...
                  </td>
                </tr>
              ) : filteredSubscriptions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-500">
                    No subscribers match the current search.
                  </td>
                </tr>
              ) : (
                filteredSubscriptions.slice(0, 100).map((item) => (
                  <tr key={item.id}>
                    <td className="p-4 align-top">
                      <div className="font-semibold text-[var(--rsi-navy)]">
                        {item.customer_name || "Unknown customer"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{item.email || "-"}</div>
                      <div className="text-xs text-gray-500">{item.phone || "-"}</div>
                    </td>
                    <td className="p-4 align-top">
                      <div className="capitalize text-[var(--rsi-navy)]">{item.platform}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {item.standalone ? "Installed app" : "Browser"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Permission: {item.notification_permission}
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <div className="flex flex-wrap gap-2">
                        {item.push_enabled ? <span className="badge-purple">Push</span> : null}
                        {item.allow_email && item.email ? (
                          <span className="badge-purple">Email</span>
                        ) : null}
                        {item.allow_new_products ? (
                          <span className="badge-purple">Products</span>
                        ) : null}
                        {item.allow_shipment_updates ? (
                          <span className="badge-purple">Shipment</span>
                        ) : null}
                        {item.allow_delivery_updates ? (
                          <span className="badge-purple">Delivery</span>
                        ) : null}
                        {item.allow_order_updates ? (
                          <span className="badge-purple">Orders</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <div className="text-xs text-gray-500">
                        Tracking: {(item.tracking_ids || []).join(", ") || "-"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Orders: {(item.order_numbers || []).join(", ") || "-"}
                      </div>
                    </td>
                    <td className="p-4 align-top text-xs text-gray-500">
                      {formatDate(item.updated_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
